import { useUserContext } from "../../../../providers/UserProvider";
import { useNotifications } from "../../../../hooks/useNotifications/useNotifications";
import { BillingNotification } from "../../../../rest-model/BillingNotification";
import { BillingNotificationToggle } from "../../../components/BillingNotificationToggle/BillingNotificationToggle";
import { useState } from "react";
import { emitToast } from "../../../../lib/toaster-utils";
import { Attention, Typography } from "@optimizely/axiom";
import { NotificationRecipientsSheet } from "../../../components/NotificationRecipientsSheet/NotificationRecipientsSheet";
import { diffRecipients, getNotificationTitle } from "../../../../lib/notification-utils";
import { Flags } from "../../../../feature-flags/flags";
import { optimizelyInstance } from "../../../../feature-flags/optimizelyInstance";
import { LoadingIndicator } from "../../../components/LoadingIndicator/LoadingIndicator";
import { useAnalyticsTracking } from "../../../../hooks/useAnalyticsTracking/useAnalyticsTracking";
import { getBillingNotificationsUpdatedPayload } from "../../../../lib/analytics-helpers";
import { useOpalBranding } from "../../../../hooks/useOpalBranding/useOpalBranding";

export const BillingNotificationsPage = () => {
    const { organizationId } = useUserContext();
    const { opalName } = useOpalBranding();
    const { billingNotifications = [], loading, updateBillingNotification } = useNotifications({ organizationId });
    const { sendTrackEvent } = useAnalyticsTracking();
    const [updating, setUpdating] = useState<number | null>(null);
    const [error, setError] = useState(null);
    const [recipientNotification, setRecipientNotification] = useState<null | BillingNotification>(null);
    const enabledProductVariation = optimizelyInstance.decide(Flags.BILLING_NOTIFICATION_ENABLED_PRODUCTS);
    const enabledProducts = (enabledProductVariation?.variables?.enabled_products as { [key: string]: boolean }) || {};
    const allowedNotifications = billingNotifications.filter((notification) => {
        return enabledProducts[notification.productId];
    });

    const handleToggle = ({
        notification,
        notificationIndex
    }: {
        notification: BillingNotification;
        notificationIndex: number;
    }) => {
        setUpdating(notificationIndex);
        setError(null);
        updateBillingNotification({
            organizationId: organizationId!,
            notificationId: notification.id,
            updates: {
                ...notification,
                enabled: !notification.enabled
            }
        })
            .then(() => {
                emitToast({ message: "Billing notification successfully updated." });
            })
            .catch((error) => {
                setError(error?.message || error);
            })

            .finally(() => setUpdating(null));
    };

    const trackRecipientChanges = ({
        notification,
        recipients
    }: {
        notification: BillingNotification;
        recipients: string[];
    }) => {
        const { usersAdded, usersRemoved } = diffRecipients(notification.recipients ?? [], recipients);

        if (!usersAdded.length && !usersRemoved.length) {
            return;
        }

        sendTrackEvent(
            getBillingNotificationsUpdatedPayload({
                instanceId: notification.instanceId,
                notificationProductName: notification.productName,
                productId: notification.productId,
                usersAdded,
                usersRemoved
            })
        );
    };

    const updateNotificationRecipients = ({ recipients }: { recipients: string[] }) => {
        const notification = recipientNotification!;
        return updateBillingNotification({
            organizationId: organizationId!,
            notificationId: notification.id,
            updates: {
                ...notification,
                recipients
            }
        }).then((savedNotification) => {
            // Diff against the persisted recipients so analytics reflects what the
            // backend actually saved, falling back to the requested list if the
            // saved notification isn't returned.
            trackRecipientChanges({ notification, recipients: savedNotification?.recipients ?? recipients });
            emitToast({ message: "Successfully saved billing recipients." });
            closeRecipientSheet();
        });
    };

    const closeRecipientSheet = () => {
        setRecipientNotification(null);
    };

    const hasExp = allowedNotifications?.find(
        (notification) => notification.productId === process.env.REACT_APP_EXPERIMENTATION_PRODUCT_ID
    );

    return (
        <div className="push-double--left push-double--right push-double--bottom content-container">
            {!!error && (
                <Attention type="bad-news" className="push-double--top">
                    {error}
                </Attention>
            )}
            <Typography type="body" tag="div" className="push-double--bottom">
                <p>
                    Select which products should send billing notifications and use the "Manage Recipients" link to add
                    any individuals who should receive the notifications. These notifications are intended to help you
                    monitor your usage throughout the month to avoid exceeding your limits and incurring additional
                    charges. If you expect to surpass your allotment, consider adjusting your plan.
                </p>
                <p>
                    When Experimentation billing notifications are enabled, you will receive notifications when your
                    application usage reaches 25%, 50%, 75%, 100% and then every 10% over 100% of your allotment.
                </p>
                <p>
                    When Content Marketing Platform billing notifications are enabled, you will receive notifications
                    when your application usage reaches 80%, 100%, and over 100% of your allotment.
                </p>
                <p>
                    When {opalName} billing notifications are enabled, you will receive monthly notifications showing
                    your credit consumption. If your application uses more than your allotment, a separate notification
                    is sent on a monthly basis until your application is no longer over your allotment.
                </p>
            </Typography>

            {loading && <LoadingIndicator height="60px" width="60px" type="spinner" />}
            {!allowedNotifications?.length && !loading && (
                <Attention type="brand">
                    Your organization does not have any products that currently support usage & billing information.
                </Attention>
            )}
            {allowedNotifications?.map((notification: BillingNotification, notificationIndex) => {
                return (
                    <BillingNotificationToggle
                        loading={updating === notificationIndex}
                        disabled={loading || updating !== null}
                        key={notificationIndex}
                        notification={notification}
                        onManageClick={() => {
                            setRecipientNotification(notification);
                        }}
                        onToggle={() => {
                            handleToggle({ notification, notificationIndex });
                        }}
                    />
                );
            })}
            {hasExp && (
                <Attention type="brand" className="push-double--top width--3-5">
                    <div className="weight--bold">Note</div>
                    Optimizely Experimentation&apos;s billing notifications are managed at the instance level. All other
                    product notifications are managed at the product level.
                </Attention>
            )}
            {!!recipientNotification && (
                <NotificationRecipientsSheet
                    onClose={closeRecipientSheet}
                    onSave={updateNotificationRecipients}
                    searchHeading="Select users to receive billing notifications"
                    subTitle={getNotificationTitle(recipientNotification)}
                    title="Billing Notification Recipients"
                    recipientEmails={recipientNotification.recipients}
                />
            )}
        </div>
    );
};

BillingNotificationsPage.displayName = "BillingNotificationsPage";
